import React, { useState, useEffect } from "react";
import config from "../Common/Configurations/APIConfig";
import apiService from "../Common/Configurations/APIService";
import SupplierHeader from "../Supplier/SupplierHeader";
import Header from "../Common/Layout/Header";
import SupplierTabs from "../Supplier/SupplierTabs";
import { useAuth } from "../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import { Link, useNavigate } from "react-router-dom";
import DeleteAlert from "../Common/Layout/DeleteAlert";
import jsonData from "../VisaData/b2bvisas.json";

// Compact Calendar Component with Appointment Highlights
const CompactCalendarPicker = ({
  selectedDate,
  onDateSelect,
  appointments,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [today] = useState(new Date());

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const fullMonthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = ["S", "M", "T", "W", "T", "F", "S"];

  // Generate years for year picker (10 years back and 10 years forward)
  const generateYears = () => {
    const currentYear = today.getFullYear();
    const years = [];
    for (let i = currentYear - 10; i <= currentYear + 10; i++) {
      years.push(i);
    }
    return years;
  };

  // Get first day of month
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  // Get last day of month
  const getLastDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const firstDay = getFirstDayOfMonth(currentMonth);
    const lastDay = getLastDayOfMonth(currentMonth);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

    const days = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  // Check if date has appointment
  const hasAppointment = (date) => {
    if (!appointments || !appointments.dates) return false;
    const dateString = formatDateToString(date);
    return appointments.dates.some((appDate) => {
      const appointmentDateStr = formatApiDateToString(appDate.appointmentDate);
      return appointmentDateStr === dateString;
    });
  };

  // Get appointment for date
  const getAppointmentForDate = (date) => {
    if (!appointments || !appointments.dates) return null;
    const dateString = formatDateToString(date);
    return appointments.dates.find((appDate) => {
      const appointmentDateStr = formatApiDateToString(appDate.appointmentDate);
      return appointmentDateStr === dateString;
    });
  };

  // Get appointment status color (only Available or Sold Out)
  const getAppointmentStatusColor = (date) => {
    const appointment = getAppointmentForDate(date);
    if (!appointment) return "";

    return appointment.status === "Available" ? "success" : "danger";
  };

  // Check if date is today
  const isToday = (date) => {
    return date.toDateString() === today.toDateString();
  };

  // Check if date is selected
  const isSelected = (date) => {
    if (!selectedDate) return false;
    const selected = new Date(selectedDate);
    return date.toDateString() === selected.toDateString();
  };

  // Check if date is in current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentMonth.getMonth();
  };

  // Check if date is disabled (past dates)
  const isDisabled = (date) => {
    return date < new Date(today.setHours(0, 0, 0, 0));
  };

  // Format date to YYYY-MM-DD
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format API date (with timezone) to YYYY-MM-DD
  const formatApiDateToString = (apiDateString) => {
    if (!apiDateString) return "";
    const date = new Date(apiDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Navigate to previous month
  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Select month
  const selectMonth = (monthIndex) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex, 1));
    setShowMonthPicker(false);
  };

  // Select year
  const selectYear = (year) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setShowYearPicker(false);
  };

  const days = generateCalendarDays();
  const years = generateYears();

  return (
    <div className="compact-calendar-picker">
      {/* Calendar Header with Month/Year Selection */}
      <div className="calendar-header d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
        <button
          className="btn btn-sm btn-outline-secondary p-1"
          onClick={prevMonth}
          type="button"
          style={{ width: "30px", height: "30px" }}
        >
          <i className="ri-arrow-left-s-line" style={{ fontSize: "14px" }}></i>
        </button>

        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary px-2 py-1"
            onClick={() => {
              setShowMonthPicker(!showMonthPicker);
              setShowYearPicker(false);
            }}
            type="button"
            style={{ fontSize: "12px", minWidth: "60px" }}
          >
            {monthNames[currentMonth.getMonth()]}
          </button>
          <button
            className="btn btn-sm btn-outline-primary px-2 py-1"
            onClick={() => {
              setShowYearPicker(!showYearPicker);
              setShowMonthPicker(false);
            }}
            type="button"
            style={{ fontSize: "12px", minWidth: "60px" }}
          >
            {currentMonth.getFullYear()}
          </button>
        </div>

        <button
          className="btn btn-sm btn-outline-secondary p-1"
          onClick={nextMonth}
          type="button"
          style={{ width: "30px", height: "30px" }}
        >
          <i className="ri-arrow-right-s-line" style={{ fontSize: "14px" }}></i>
        </button>
      </div>

      {/* Month Picker */}
      {showMonthPicker && (
        <div className="month-picker border rounded p-2 bg-white mb-2">
          <div className="row g-1">
            {fullMonthNames.map((month, index) => (
              <div key={month} className="col-4">
                <button
                  className={`btn btn-sm w-100 ${
                    currentMonth.getMonth() === index
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => selectMonth(index)}
                  style={{ fontSize: "11px", padding: "4px 2px" }}
                  type="button"
                >
                  {monthNames[index]}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Year Picker */}
      {showYearPicker && (
        <div
          className="year-picker border rounded p-2 bg-white mb-2"
          style={{ maxHeight: "150px", overflowY: "auto" }}
        >
          <div className="row g-1">
            {years.map((year) => (
              <div key={year} className="col-4">
                <button
                  className={`btn btn-sm w-100 ${
                    currentMonth.getFullYear() === year
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => selectYear(year)}
                  style={{ fontSize: "11px", padding: "4px 2px" }}
                  type="button"
                >
                  {year}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Day Names */}
      <div className="row day-names mb-1 g-0">
        {dayNames.map((day) => (
          <div
            key={day}
            className="col text-center"
            style={{ fontSize: "11px", fontWeight: "bold", color: "#6c757d" }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {days.map((date, index) => {
          const dateString = formatDateToString(date);
          const disabled = isDisabled(date);
          const today = isToday(date);
          const selected = isSelected(date);
          const currentMonth = isCurrentMonth(date);
          const hasAppt = hasAppointment(date);
          const appointment = getAppointmentForDate(date);
          const statusColor = getAppointmentStatusColor(date);

          return (
            <div
              key={index}
              className={`calendar-day text-center p-1 border rounded position-relative ${
                disabled
                  ? "text-muted bg-light"
                  : selected
                  ? "bg-primary text-white"
                  : today
                  ? "bg-warning text-dark"
                  : !currentMonth
                  ? "text-muted bg-light"
                  : hasAppt
                  ? `bg-${statusColor} text-white`
                  : "bg-white"
              }`}
              onClick={() => !disabled && onDateSelect(dateString, appointment)}
              style={{
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.5 : 1,
                fontSize: "12px",
                minHeight: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {date.getDate()}
              {hasAppt && (
                <div className="position-absolute top-0 start-100 translate-middle p-1">
                  <div
                    className={`badge badge-dot bg-${statusColor}`}
                    style={{ width: "6px", height: "6px" }}
                  ></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected Date Display */}
      {selectedDate && (
        <div className="selected-date mt-2 p-2 bg-light rounded text-center">
          <small className="text-muted" style={{ fontSize: "11px" }}>
            Selected:
          </small>
          <div className="fw-bold" style={{ fontSize: "12px" }}>
            {new Date(selectedDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="calendar-legend mt-3">
        <div className="row g-2 text-center">
          <div className="col-6">
            <div className="d-flex align-items-center justify-content-center">
              <div
                className="bg-success rounded me-1"
                style={{ width: "12px", height: "12px" }}
              ></div>
              <small style={{ fontSize: "10px" }}>Available</small>
            </div>
          </div>
          <div className="col-6">
            <div className="d-flex align-items-center justify-content-center">
              <div
                className="bg-danger rounded me-1"
                style={{ width: "12px", height: "12px" }}
              ></div>
              <small style={{ fontSize: "10px" }}>Sold Out</small>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .compact-calendar-picker {
          max-width: 280px;
          margin: 0 auto;
        }
        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }
        .calendar-day {
          transition: all 0.2s ease;
        }
        .month-picker,
        .year-picker {
          position: absolute;
          left: 0;
          right: 0;
          z-index: 10;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .badge-dot {
          padding: 0;
          border-radius: 50%;
        }
      `}</style>
    </div>
  );
};

const initialDateValues = {
  appointmentDate: "",
  noOfSlots: 1,
  price: 0,
  status: "Available",
};

const VisaAppointments = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const [existingAppointments, setExistingAppointments] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [addShow, setAddShow] = useState(false);
  const [selectedVisa, setSelectedVisa] = useState(null);
  const [dateValues, setDateValues] = useState(initialDateValues);
  const [selectedVisaFilter, setSelectedVisaFilter] = useState("All");
  const [visaOptions, setVisaOptions] = useState([]);
  const [currentAppointments, setCurrentAppointments] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use dynamic supplierCompanyId from AuthContext (companyId)
  const supplierCompanyId = user?.cid;

  // Get all visas from JSON data
  const getAllVisas = () => {
    return jsonData.flatMap((country) =>
      country.visas.map((visa) => ({
        ...visa,
        countryName: country.name,
        flagSrc: country.flagSrc,
        countryCode: country.countryCode,
      }))
    );
  };

  // Get visa options for dropdown (grouped by country)
  const getVisaOptions = () => {
    const options = [];

    // Add "All" option
    options.push({
      value: "All",
      label: "All Visas",
      countryName: "All",
    });

    // Add visas grouped by country
    jsonData.forEach((country) => {
      country.visas.forEach((visa) => {
        options.push({
          value: visa.visaId,
          label: `${country.name} - ${visa.visaName}`,
          countryName: country.name,
          visaName: visa.visaName,
          flagSrc: country.flagSrc,
        });
      });
    });

    return options;
  };

  const [allVisas, setAllVisas] = useState([]);
  const [filteredVisas, setFilteredVisas] = useState([]);

  // Format API date to YYYY-MM-DD
  const formatApiDateToString = (apiDateString) => {
    if (!apiDateString) return "";
    const date = new Date(apiDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Get all visa appointments
  const GetAllAppointments = async () => {
    try {
      const res = await apiService.getAll(config.GETALLVISAAPPOINTMENTS);

      if (res && Array.isArray(res)) {
        setExistingAppointments(res);
      } else {
        setExistingAppointments([]);
      }
    } catch (error) {
      console.error("Error fetching all visa appointments:", error);
      setExistingAppointments([]);
    }
  };

  // Find existing appointment for a visa
  const getAppointmentForVisa = (visaId) => {
    const appointment = existingAppointments.find(
      (app) =>
        app.visaId === visaId && app.supplierCompanyId === supplierCompanyId
    );
    return appointment;
  };

  const handleAddShow = async (visa) => {
    setSelectedVisa(visa);
    setDateValues(initialDateValues);

    // Load existing appointments for this visa
    const appointments = getAppointmentForVisa(visa.visaId);

    // Process dates to fix format
    if (appointments && appointments.dates) {
      const processedAppointments = {
        ...appointments,
        dates: appointments.dates.map((date) => ({
          ...date,
          appointmentDate: formatApiDateToString(date.appointmentDate),
        })),
      };
      setCurrentAppointments(processedAppointments);
    } else {
      setCurrentAppointments(appointments);
    }

    setAddShow(true);
  };

  const handleAddClose = () => {
    if (!isSubmitting) {
      setAddShow(false);
      setSelectedVisa(null);
      setDateValues(initialDateValues);
      setCurrentAppointments(null);
    }
  };

  const handleOpenDeleteModal = (visaId) => {
    setItemToDelete(visaId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // Handle calendar date selection
  const handleCalendarDateSelect = (dateString, existingAppointment) => {
    if (existingAppointment) {
      // If date has existing appointment, load it into form for editing
      setDateValues({
        appointmentDate: dateString,
        noOfSlots: existingAppointment.noOfSlots,
        price: existingAppointment.price,
        status: existingAppointment.status,
      });
    } else {
      // If no appointment, set just the date with default values
      setDateValues({
        appointmentDate: dateString,
        noOfSlots: 1,
        price: 0,
        status: "Available",
      });
    }
  };

  // Handle visa filter change
  const handleVisaFilterChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedVisaFilter(selectedValue);

    if (selectedValue === "All") {
      setFilteredVisas(allVisas);
    } else {
      const filtered = allVisas.filter((visa) => visa.visaId === selectedValue);
      setFilteredVisas(filtered);
    }
  };

  // Reset filter
  const handleResetFilter = () => {
    setSelectedVisaFilter("All");
    setFilteredVisas(allVisas);
  };

  // Handle input changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateValues({
      ...dateValues,
      [name]: name === "noOfSlots" || name === "price" ? Number(value) : value,
    });
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        const appointment = getAppointmentForVisa(itemToDelete);
        if (appointment) {
          await apiService.remove(
            config.DELETEVISAAPPOINTMENT,
            appointment.visaAppointmentId
          );
          await GetAllAppointments(); // Refresh data after delete
        }
      } catch (error) {
        console.error("Error deleting visa appointment:", error);
      }
      handleCloseDeleteModal();
    }
  };

  const validate = () => {
    if (!dateValues.appointmentDate) return false;
    if (dateValues.noOfSlots <= 0) return false;
    if (dateValues.price < 0) return false;
    return true;
  };

  // Update local appointments immediately after successful submission
  const updateLocalAppointments = (newDateData) => {
    if (!currentAppointments) {
      // Create new appointment object
      const newAppointment = {
        visaId: selectedVisa.visaId,
        supplierCompanyId: supplierCompanyId, // Use dynamic companyId
        dates: [newDateData],
      };
      setCurrentAppointments(newAppointment);
    } else {
      // Update existing appointments
      const existingDateIndex = currentAppointments.dates.findIndex(
        (date) => date.appointmentDate === dateValues.appointmentDate
      );

      let updatedDates;

      if (existingDateIndex !== -1) {
        // Update existing date
        updatedDates = [...currentAppointments.dates];
        updatedDates[existingDateIndex] = newDateData;
      } else {
        // Add new date
        updatedDates = [...currentAppointments.dates, newDateData];
      }

      setCurrentAppointments({
        ...currentAppointments,
        dates: updatedDates,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      alert("Please fill all fields correctly");
      return;
    }

    setIsSubmitting(true);
    setBtnLoading(true);

    try {
      const existingApp = getAppointmentForVisa(selectedVisa.visaId);

      // Prepare the date data for API
      const dateDataForApi = {
        appointmentDate: new Date(dateValues.appointmentDate).toISOString(),
        noOfSlots: dateValues.noOfSlots,
        price: dateValues.price,
        status: dateValues.status,
      };

      // Prepare the date data for local state (with formatted date)
      const dateDataForLocal = {
        appointmentDate: dateValues.appointmentDate,
        noOfSlots: dateValues.noOfSlots,
        price: dateValues.price,
        status: dateValues.status,
      };

      if (existingApp) {
        // Update existing appointment - add new date or update existing date
        const existingDateIndex = existingApp.dates.findIndex(
          (date) =>
            formatApiDateToString(date.appointmentDate) ===
            dateValues.appointmentDate
        );

        let updatedDates;

        if (existingDateIndex !== -1) {
          // Update existing date
          updatedDates = [...existingApp.dates];
          updatedDates[existingDateIndex] = dateDataForApi;
        } else {
          // Adding new date
          updatedDates = [...existingApp.dates, dateDataForApi];
        }

        await apiService.update(config.UPDATEVISAAPPOINTMENT, {
          visaAppointmentId: existingApp.visaAppointmentId,
          visaId: existingApp.visaId,
          supplierCompanyId: supplierCompanyId, // Use dynamic companyId
          dates: updatedDates,
        });
      } else {
        // Create new appointment
        const formData = {
          visaId: selectedVisa.visaId,
          supplierCompanyId: supplierCompanyId, // Use dynamic companyId
          dates: [dateDataForApi],
        };
        await apiService.create(config.CREATEVISAAPPOINTMENT, formData);
      }

      // Update local state immediately without closing modal
      updateLocalAppointments(dateDataForLocal);

      // Also refresh the global appointments
      await GetAllAppointments();

      // Reset form but keep modal open
      setDateValues(initialDateValues);

      // Show success message
    } catch (error) {
    } finally {
      setBtnLoading(false);
      setIsSubmitting(false);
    }
  };

  // Check if selected date has existing appointment
  const hasExistingAppointment = () => {
    if (!currentAppointments || !currentAppointments.dates) return false;
    const hasAppt = currentAppointments.dates.some(
      (date) => date.appointmentDate === dateValues.appointmentDate
    );
    return hasAppt;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const dateObj = new Date(dateString);
    return dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  useEffect(() => {
    if (user && !loading) {
      const visas = getAllVisas();
      setAllVisas(visas);
      setFilteredVisas(visas);
      setVisaOptions(getVisaOptions());
      GetAllAppointments(); // Call the correct API to get all appointments
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      {/* <SupplierHeader User={user} /> */}
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  {/* <SupplierTabs pageName="APPOINTMENTS" /> */}
                </div>

                {/* Visa Filter */}
                <div className="card-header">
                  <div className="row g-3 align-items-end">
                    <div className="col-md-6">
                      <label htmlFor="visaFilter" className="form-label">
                        Filter by Visa
                      </label>
                      <select
                        className="form-select"
                        id="visaFilter"
                        value={selectedVisaFilter}
                        onChange={handleVisaFilterChange}
                      >
                        {visaOptions.map((option, index) => (
                          <option key={index} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-danger w-100"
                        onClick={handleResetFilter}
                      >
                        <i className="ri-refresh-line me-1"></i>
                        Reset
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="table-responsive table-card p-2">
                    <table className="table align-middle">
                      <thead className="bg-soft-success bg-gradient">
                        <tr>
                          <th>S.No</th>
                          <th>Country & Visa Type</th>
                          <th>Validity</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredVisas.length > 0 ? (
                          filteredVisas.map((visa, index) => {
                            const appointment = getAppointmentForVisa(
                              visa.visaId
                            );
                            const hasAppointments =
                              appointment &&
                              appointment.dates &&
                              appointment.dates.length > 0;

                            return (
                              <tr key={visa.visaId}>
                                <td>{index + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <img
                                      src={`https://flagcdn.com/w20/${visa.flagSrc.toLowerCase()}.png`}
                                      alt={visa.countryName}
                                      className="me-2"
                                      style={{ width: "20px", height: "15px" }}
                                    />
                                    <div>
                                      <div className="fw-bold">
                                        {visa.countryName}
                                      </div>
                                      <div className="text-muted small">
                                        {visa.visaName}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className="badge bg-primary">
                                    {visa.validity}
                                  </span>
                                </td>
                                <td>
                                  <ul className="list-inline hstack gap-2 mb-0">
                                    <li
                                      className="list-inline-item"
                                      title="Manage Appointments"
                                    >
                                      <Link
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleAddShow(visa)}
                                      >
                                        <i className="ri-calendar-line align-bottom me-1" />
                                        {hasAppointments
                                          ? "Add Slots"
                                          : "Add Slots"}
                                      </Link>
                                    </li>
                                    {hasAppointments && (
                                      <li
                                        className="list-inline-item"
                                        title="Delete All Appointments"
                                      >
                                        <Link
                                          className="btn btn-sm btn-danger"
                                          onClick={() =>
                                            handleOpenDeleteModal(visa.visaId)
                                          }
                                        >
                                          <i className="ri-delete-bin-line align-bottom" />
                                        </Link>
                                      </li>
                                    )}
                                  </ul>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center">
                              No visas found for selected filter
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Date Modal */}
        <Modal
          show={addShow}
          onHide={handleAddClose}
          size="lg"
          backdrop={isSubmitting ? "static" : true}
          keyboard={!isSubmitting}
        >
          <Modal.Header closeButton={!isSubmitting}>
            <Modal.Title>
              {selectedVisa
                ? `Manage Appointments - ${selectedVisa.countryName} - ${selectedVisa.visaName}`
                : "Manage Appointments"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedVisa && (
              <div className="alert alert-info mb-3">
                <div className="d-flex align-items-center">
                  <img
                    src={`https://flagcdn.com/w20/${selectedVisa.flagSrc.toLowerCase()}.png`}
                    alt={selectedVisa.countryName}
                    className="me-2"
                    style={{ width: "20px", height: "15px" }}
                  />
                  <div>
                    <strong>
                      {selectedVisa.countryName} - {selectedVisa.visaName}
                    </strong>
                    <div className="small">
                      Validity: {selectedVisa.validity}
                    </div>
                    {currentAppointments && (
                      <div className="small mt-1">
                        <span className="badge bg-secondary">
                          {currentAppointments.dates
                            ? currentAppointments.dates.length
                            : 0}{" "}
                          appointment(s) found
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} autoComplete="off" noValidate>
              <div className="row">
                {/* Compact Calendar Picker */}
                <div className="col-lg-5 mb-3">
                  <label className="form-label fw-bold">Select Date</label>
                  <CompactCalendarPicker
                    selectedDate={dateValues.appointmentDate}
                    onDateSelect={handleCalendarDateSelect}
                    appointments={currentAppointments}
                  />
                </div>

                {/* Status and Details Section */}
                <div className="col-lg-7">
                  <div className="row">
                    {/* Status Field - Only Available or Sold Out */}
                    <div className="col-12 mb-3">
                      <label htmlFor="status" className="form-label">
                        Slot Status
                      </label>
                      <select
                        name="status"
                        value={dateValues.status}
                        onChange={handleDateChange}
                        className="form-select"
                        disabled={isSubmitting}
                      >
                        <option value="Available">Available</option>
                        <option value="Sold Out">Sold Out</option>
                      </select>
                    </div>

                    {/* Number of Slots */}
                    <div className="col-12 mb-3">
                      <label className="form-label">Number of Slots</label>
                      <input
                        type="number"
                        name="noOfSlots"
                        value={dateValues.noOfSlots}
                        onChange={handleDateChange}
                        className="form-control"
                        min="1"
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Price */}
                    <div className="col-12 mb-3">
                      <label className="form-label">Price (INR)</label>
                      <input
                        type="number"
                        name="price"
                        value={dateValues.price}
                        onChange={handleDateChange}
                        className="form-control"
                        min="0"
                        step="0.01"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Selected Date Summary */}
                  {dateValues.appointmentDate && (
                    <div
                      className={`alert ${
                        hasExistingAppointment()
                          ? "alert-warning"
                          : "alert-success"
                      } mt-3`}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>
                            {hasExistingAppointment()
                              ? "Update Existing Appointment"
                              : "Create New Appointment"}
                          </strong>
                          <div className="small">
                            Date: {formatDate(dateValues.appointmentDate)} |
                            Slots: {dateValues.noOfSlots} | Price: ₹
                            {dateValues.price} | Status:{" "}
                            <span
                              className={`badge bg-${
                                dateValues.status === "Available"
                                  ? "success"
                                  : "danger"
                              }`}
                            >
                              {dateValues.status}
                            </span>
                          </div>
                          {hasExistingAppointment() && (
                            <div className="small text-muted mt-1">
                              <i className="ri-information-line me-1"></i>
                              You are updating an existing appointment
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Modal.Footer>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    btnLoading || !dateValues.appointmentDate || isSubmitting
                  }
                >
                  {btnLoading
                    ? "Please wait..."
                    : hasExistingAppointment()
                    ? "Update Slot"
                    : "Add Slot"}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleAddClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </Modal.Footer>
            </form>
          </Modal.Body>
        </Modal>

        {/* Delete Appointment Modal */}
        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
          title="Delete All Appointments"
          message="Are you sure you want to delete all appointments for this visa? This will remove all dates and cannot be undone."
        />
      </div>
    </div>
  );
};

export default VisaAppointments;
